import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminComuni = () => {
  const [comuni, setComuni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComune, setNewComune] = useState("");
  const [newZone, setNewZone] = useState("UC");
  const [newType, setNewType] = useState("concordare");

  useEffect(() => { fetchComuni(); }, []);

  const fetchComuni = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.get(`${API}/admin/delivery-comuni`, { headers: { Authorization: `Bearer ${token}` } });
      setComuni(res.data || []);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };

  const handleToggle = async (comuneId) => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.put(`${API}/admin/delivery-comuni/${comuneId}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.name}: ${res.data.active ? "attivato" : "disattivato"}`);
      fetchComuni();
    } catch { toast.error("Errore"); }
  };

  const handleAdd = async () => {
    if (!newComune.trim()) { toast.error("Inserisci il nome del comune"); return; }
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/delivery-comuni`, { name: newComune.trim(), zone: newZone, type: newType }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Comune aggiunto");
      setNewComune("");
      fetchComuni();
    } catch (error) { toast.error(error.response?.data?.detail || "Errore"); }
  };

  const handleDelete = async (comuneId, name) => {
    if (!window.confirm(`Eliminare ${name}?`)) return;
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/delivery-comuni/${comuneId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Comune eliminato");
      fetchComuni();
    } catch { toast.error("Errore eliminazione"); }
  };

  const diretti = comuni.filter(c => c.type === "diretto");
  const concordare = comuni.filter(c => c.type === "concordare");

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center h-64"><div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  const ComuneRow = ({ comune }) => (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${comune.active ? "bg-zinc-900 border-zinc-800" : "bg-zinc-900/50 border-zinc-800/50 opacity-60"}`}>
      <div className="flex items-center gap-3">
        <button onClick={() => handleToggle(comune.id)} className="shrink-0">
          {comune.active
            ? <ToggleRight className="w-8 h-8 text-green-500" />
            : <ToggleLeft className="w-8 h-8 text-zinc-600" />
          }
        </button>
        <div>
          <p className={`font-medium ${comune.active ? "text-white" : "text-zinc-500"}`}>{comune.name}</p>
          <p className="text-zinc-600 text-xs">Zona {comune.zone}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-xs px-2 py-1 rounded ${comune.active ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"}`}>
          {comune.active ? "Attivo" : "Disattivato"}
        </span>
        {comune.type !== "diretto" && (
          <button onClick={() => handleDelete(comune.id, comune.name)} className="text-red-400 hover:text-red-300 p-1">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-comuni">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <MapPin className="w-8 h-8 text-green-500" />
          Comuni di Consegna
        </h1>

        {/* Aggiungi comune */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <h3 className="text-white font-bold mb-3">Aggiungi Comune</h3>
          <div className="flex gap-3">
            <input
              type="text"
              value={newComune}
              onChange={(e) => setNewComune(e.target.value)}
              placeholder="Nome comune..."
              className="flex-1 bg-black border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-green-500"
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <select value={newZone} onChange={(e) => setNewZone(e.target.value)}
              className="bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
              <option value="A">Zona A (Foligno)</option>
              <option value="B">Zona B (Assisi)</option>
              <option value="UC">Concordare</option>
            </select>
            <select value={newType} onChange={(e) => setNewType(e.target.value)}
              className="bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
              <option value="diretto">Consegna diretta</option>
              <option value="concordare">Da concordare</option>
            </select>
            <button onClick={handleAdd} className="bg-green-500 text-black px-4 py-2 rounded-lg font-bold hover:bg-green-600 flex items-center gap-2 shrink-0">
              <Plus className="w-5 h-5" /> Aggiungi
            </button>
          </div>
        </div>

        {/* Consegna diretta */}
        <div>
          <h2 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-full"></span>
            Consegna Diretta ({diretti.length})
          </h2>
          <div className="space-y-2">
            {diretti.map(c => <ComuneRow key={c.id} comune={c} />)}
          </div>
        </div>

        {/* Da concordare */}
        <div>
          <h2 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
            <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
            Consegna da Concordare ({concordare.length})
          </h2>
          <div className="space-y-2">
            {concordare.length === 0 ? (
              <p className="text-zinc-500 text-sm">Nessun comune configurato</p>
            ) : (
              concordare.map(c => <ComuneRow key={c.id} comune={c} />)
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminComuni;
